import { useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { ScanLine, Loader2, Trash2, Plus, Upload, FileText, X as XIcon, Info } from "lucide-react";

const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function ScanInventoryDialog({ open, onOpenChange, onSuccess, defaultCategory = "office" }) {
  const [files, setFiles] = useState([]); // {id, file, dataUrl, isPdf, name}
  const [scanning, setScanning] = useState(false);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [upsert, setUpsert] = useState(true);
  const [category, setCategory] = useState(defaultCategory);
  const inputRef = useRef(null);

  const reset = () => {
    setFiles([]);
    setRows([]);
    setScanning(false);
    setSaving(false);
  };

  const addFiles = async (fileList) => {
    const chosen = Array.from(fileList || []);
    if (!chosen.length) return;
    const supported = /\.(png|jpe?g|webp|pdf)$/i;
    const bad = chosen.filter((f) => !supported.test(f.name));
    if (bad.length) toast.error(`Skipped ${bad.length} unsupported file(s)`);
    const good = chosen.filter((f) => supported.test(f.name));
    const withData = await Promise.all(
      good.map(async (file) => ({
        id: crypto.randomUUID(),
        file,
        dataUrl: await readFileAsDataURL(file),
        isPdf: /\.pdf$/i.test(file.name),
        name: file.name,
      }))
    );
    setFiles((prev) => [...prev, ...withData]);
    setRows([]);
  };

  const removeFile = (id) => setFiles((fs) => fs.filter((f) => f.id !== id));

  const scan = async () => {
    if (files.length === 0) return;
    setScanning(true);
    try {
      const payload = {
        files: files.map((f) => ({
          image_base64: f.dataUrl,
          mime_type: f.isPdf ? "application/pdf" : (f.file.type || "image/jpeg"),
        })),
        category,
      };
      const { data } = await api.post("/inventory/scan-batch", payload);
      if (!data.items?.length) {
        toast.error("No items detected — try clearer pages");
      } else {
        toast.success(`Detected ${data.items.length} items across ${data.pages_scanned} page${data.pages_scanned === 1 ? "" : "s"} — review before saving`);
        setRows(data.items.map((r, i) => ({ ...r, _key: crypto.randomUUID() })));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const updateRow = (i, field, value) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));
  const addRow = () => setRows((rs) => [...rs, { sku: "", name: "", unit: "each", quantity: 0, _key: crypto.randomUUID() }]);

  const save = async () => {
    const valid = rows
      .map((r) => ({
        sku: (r.sku || "").trim().toUpperCase(),
        name: (r.name || "").trim(),
        unit: (r.unit || "each").trim(),
        quantity: Math.max(0, Number(r.quantity) || 0),
      }))
      .filter((r) => r.sku && r.name);
    if (valid.length === 0) return toast.error("Nothing to save — every row needs a SKU and a name");
    setSaving(true);
    try {
      const { data } = await api.post("/inventory/items/bulk", { items: valid, upsert, category });
      const parts = [];
      if (data.created) parts.push(`${data.created} added`);
      if (data.updated) parts.push(`${data.updated} restocked`);
      if (data.skipped) parts.push(`${data.skipped} skipped`);
      toast.success(`Import complete — ${parts.join(", ") || "no changes"}`);
      reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-4xl rounded-md" data-testid="scan-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="grid place-items-center h-10 w-10 rounded-sm bg-blue-100 text-blue-800">
              <ScanLine size={20} />
            </div>
            <div>
              <DialogTitle className="font-display text-2xl font-black tracking-tight">
                Scan Inventory List
              </DialogTitle>
              <DialogDescription>
                Upload as many pages as you need — images or a multi-page PDF from your printer. The AI reads every line and merges the results.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="md:col-span-2 space-y-3">
            <div className="border border-slate-200 rounded-sm p-2 bg-blue-50">
              <div className="text-[10px] font-bold uppercase tracking-widest text-blue-800 mb-1.5">Scan into</div>
              <div className="inline-flex rounded-sm border border-blue-200 overflow-hidden bg-white">
                <button
                  type="button"
                  onClick={() => setCategory("office")}
                  data-testid="scan-cat-office"
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${category === "office" ? "bg-blue-800 text-white" : "text-slate-700 hover:bg-blue-50"}`}
                >
                  Office
                </button>
                <button
                  type="button"
                  onClick={() => setCategory("janitorial")}
                  data-testid="scan-cat-janitorial"
                  className={`px-3 py-1.5 text-xs font-semibold border-l border-blue-200 transition-colors ${category === "janitorial" ? "bg-blue-800 text-white" : "text-slate-700 hover:bg-blue-50"}`}
                >
                  Janitorial
                </button>
              </div>
            </div>
            <div
              onClick={() => inputRef.current?.click()}
              className="border-2 border-dashed border-slate-300 rounded-md p-4 min-h-[220px] flex items-center justify-center cursor-pointer hover:border-blue-800 transition-colors bg-slate-50"
              data-testid="scan-dropzone"
            >
              {files.length === 0 ? (
                <div className="text-center">
                  <Upload size={28} className="mx-auto text-slate-400" />
                  <div className="mt-2 text-sm font-semibold text-slate-700">Pick images or a PDF</div>
                  <div className="text-xs text-slate-500 mt-1">Multi-select supported · PNG, JPG, WEBP, PDF</div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 w-full">
                  {files.map((f) => (
                    <div key={f.id} className="relative aspect-square rounded-sm overflow-hidden border border-slate-200 bg-white" data-testid={`scan-thumb-${f.id}`}>
                      {f.isPdf ? (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-red-50 p-2">
                          <FileText size={22} className="text-red-700" />
                          <div className="mt-1 text-[10px] font-mono text-red-800 text-center truncate w-full" title={f.name}>{f.name}</div>
                        </div>
                      ) : (
                        <img src={f.dataUrl} alt={f.name} className="w-full h-full object-cover" />
                      )}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeFile(f.id); }}
                        className="absolute top-1 right-1 h-5 w-5 grid place-items-center rounded-sm bg-white/95 text-slate-700 hover:text-red-600 shadow"
                        aria-label={`Remove ${f.name}`}
                        data-testid={`scan-thumb-remove-${f.id}`}
                      >
                        <XIcon size={12} />
                      </button>
                    </div>
                  ))}
                  <div className="aspect-square rounded-sm border border-dashed border-slate-300 flex items-center justify-center text-slate-500 hover:border-blue-800 hover:text-blue-800">
                    <Plus size={20} />
                  </div>
                </div>
              )}
            </div>
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,application/pdf"
              multiple
              capture="environment"
              onChange={(e) => addFiles(e.target.files)}
              className="hidden"
              data-testid="scan-file-input"
            />
            <div className="flex items-start gap-2 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-2">
              <Info size={12} className="mt-0.5 shrink-0" />
              <span>
                Scanning from a printer? Use your printer's "Scan to PDF" or "Scan to Computer" feature, then upload the file here. Browsers can't talk to printers directly.
              </span>
            </div>
            <Button
              onClick={scan}
              disabled={files.length === 0 || scanning}
              data-testid="scan-btn"
              className="w-full h-11 rounded-sm bg-blue-800 hover:bg-blue-900 text-white font-semibold"
            >
              {scanning ? <Loader2 size={16} className="animate-spin mr-1.5" /> : <ScanLine size={16} className="mr-1.5" />}
              {rows.length ? "Re-scan" : `Scan ${files.length || ""} file${files.length === 1 ? "" : "s"}`.trim()}
            </Button>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={upsert} onChange={(e) => setUpsert(e.target.checked)} data-testid="scan-upsert-cb" />
              If SKU already exists, add to its stock instead of skipping
            </label>
          </div>

          <div className="md:col-span-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">
                Detected Rows ({rows.length})
              </Label>
              {rows.length > 0 && (
                <Button type="button" onClick={addRow} variant="outline" size="sm" className="rounded-sm" data-testid="scan-add-row">
                  <Plus size={14} className="mr-1" /> Add Row
                </Button>
              )}
            </div>
            {rows.length === 0 ? (
              <div className="border border-dashed border-slate-300 rounded-md p-12 text-center text-sm text-slate-500 font-mono">
                {scanning ? "Reading your pages…" : "Detected rows will appear here after scanning."}
              </div>
            ) : (
              <div className="border border-slate-200 rounded-md overflow-x-auto max-h-[420px] overflow-y-auto">
                <table className="w-full text-sm" data-testid="scan-rows-table">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                    <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      <th className="p-2">SKU</th>
                      <th className="p-2">Name</th>
                      <th className="p-2 w-20">Unit</th>
                      <th className="p-2 w-20 text-right">Qty</th>
                      <th className="p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r._key} className="border-b border-slate-200 last:border-b-0" data-testid={`scan-row-${i}`}>
                        <td className="p-1.5">
                          <Input value={r.sku} onChange={(e) => updateRow(i, "sku", e.target.value)} className="h-8 rounded-sm font-mono uppercase text-xs" data-testid={`scan-sku-${i}`} />
                        </td>
                        <td className="p-1.5">
                          <Input value={r.name} onChange={(e) => updateRow(i, "name", e.target.value)} className="h-8 rounded-sm text-sm" data-testid={`scan-name-${i}`} />
                        </td>
                        <td className="p-1.5">
                          <Input value={r.unit} onChange={(e) => updateRow(i, "unit", e.target.value)} className="h-8 rounded-sm text-xs" />
                        </td>
                        <td className="p-1.5">
                          <Input type="number" min="0" value={r.quantity} onChange={(e) => updateRow(i, "quantity", e.target.value)} className="h-8 rounded-sm font-mono text-right text-xs" data-testid={`scan-qty-${i}`} />
                        </td>
                        <td className="p-1.5 text-right">
                          <button
                            onClick={() => removeRow(i)}
                            className="text-slate-400 hover:text-red-600"
                            aria-label="Remove"
                            data-testid={`scan-remove-${i}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => { reset(); onOpenChange(false); }} className="rounded-sm">Cancel</Button>
          <Button
            onClick={save}
            disabled={saving || rows.length === 0}
            data-testid="scan-save-btn"
            className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm"
          >
            {saving && <Loader2 size={16} className="animate-spin mr-1.5" />}
            Save {rows.length ? `${rows.length} ` : ""}Item{rows.length === 1 ? "" : "s"} to Inventory
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
